import type Matter from 'matter-js';
import type { Container } from 'pixi.js';
import { FallingBlock } from '@/canvas/components/FallingBlock';
import type { BlockManager } from '@/canvas/systems/BlockManager';
import type { ExtendedMatterBody, IPhysicsEngine } from '@/canvas/types/Block';
import { useGameStore } from '@/store/gameStore';
import { useSeStore } from '@/store/seStore';

interface MergeQueueEntry {
  bodyA: ExtendedMatterBody;
  bodyB: ExtendedMatterBody;
  blockId: number;
}

/**
 * 接触判定と進化システム
 */
export class CollisionHandler {
  private blockManager: BlockManager;
  private container: Container;
  private physicsEngine: IPhysicsEngine;
  private enableBlockMerge: boolean;
  private activeBlocks: Set<FallingBlock> | null = null;
  private collisionCallback:
    | ((event: Matter.IEventCollision<Matter.Engine>) => void)
    | null = null;
  private playMergeSound: () => void;
  private addScore: (points: number) => void;
  private incrementBlockCount: (blockId: number) => void;

  /**
   * collisionStart 内でキューに積み、Matter.Engine.update 完了後に処理する。
   * イベントコールバック中にワールドを書き換えると Resolver が削除済みボディを
   * 参照してクラッシュするため、遅延実行が必須。
   */
  private mergeQueue: MergeQueueEntry[] = [];

  constructor(
    blockManager: BlockManager,
    container: Container,
    physicsEngine: IPhysicsEngine,
    enableBlockMerge: boolean = true,
  ) {
    this.blockManager = blockManager;
    this.container = container;
    this.physicsEngine = physicsEngine;
    this.enableBlockMerge = enableBlockMerge;
    this.playMergeSound = useSeStore.getState().playMergeSound;
    const { addScore, incrementBlockCount } = useGameStore.getState();
    this.addScore = addScore;
    this.incrementBlockCount = incrementBlockCount;
    this.setupCollisionDetection();
  }

  /** GameEngine から activeBlocks セットの参照を受け取る */
  setActiveBlocks(activeBlocks: Set<FallingBlock>): void {
    this.activeBlocks = activeBlocks;
  }

  private setupCollisionDetection(): void {
    this.collisionCallback = (event: Matter.IEventCollision<Matter.Engine>) => {
      const pairs = event.pairs;

      for (const pair of pairs) {
        const bodyA = pair.bodyA as ExtendedMatterBody;
        const bodyB = pair.bodyB as ExtendedMatterBody;

        if (
          bodyA.label?.startsWith('boundary') ||
          bodyB.label?.startsWith('boundary')
        ) {
          continue;
        }

        const blockIdA = bodyA.blockId;
        const blockIdB = bodyB.blockId;

        if (typeof blockIdA === 'number' && typeof blockIdB === 'number') {
          if (blockIdA === blockIdB && this.enableBlockMerge) {
            // isMerging / isQueuedForMerge / isStatic のいずれかが立っていれば skip
            if (
              bodyA.isMerging ||
              bodyB.isMerging ||
              bodyA.isQueuedForMerge ||
              bodyB.isQueuedForMerge ||
              bodyA.isStatic ||
              bodyB.isStatic
            ) {
              continue;
            }
            // キューに積むだけ。実際の削除・生成は processMergeQueue() で行う
            bodyA.isQueuedForMerge = true;
            bodyB.isQueuedForMerge = true;
            this.mergeQueue.push({ bodyA, bodyB, blockId: blockIdA });
          }
        }
      }
    };

    this.physicsEngine.onCollision(this.collisionCallback);
  }

  /**
   * Matter.Engine.update 完了後（ゲームループ内）に呼び出す。
   * キューに積まれた結合ペアをここで処理することで、
   * Resolver 実行中のワールド書き換えによるクラッシュを防ぐ。
   */
  processMergeQueue(): void {
    if (this.mergeQueue.length === 0) return;

    const queue = this.mergeQueue;
    this.mergeQueue = [];

    for (const entry of queue) {
      this.handleBlockMerge(entry.bodyA, entry.bodyB, entry.blockId);
    }
  }

  private handleBlockMerge(
    bodyA: ExtendedMatterBody,
    bodyB: ExtendedMatterBody,
    blockId: number,
  ): void {
    // 多重防御: processMergeQueue 到達時点で既に処理済みなら skip
    if (bodyA.isMerging || bodyB.isMerging) {
      return;
    }

    if (bodyA.isStatic || bodyB.isStatic) return;

    this.playMergeSound();

    const nextBlockId = this.blockManager.getNextBlockId(blockId);
    if (nextBlockId === null) {
      this.removeBlocks(bodyA, bodyB);
      return;
    }

    const newX = (bodyA.position.x + bodyB.position.x) / 2;
    const newY = (bodyA.position.y + bodyB.position.y) / 2;

    const currentBlockData = this.blockManager.getBlockData(blockId);
    const score = currentBlockData?.score ?? 0;
    if (score > 0) {
      this.addScore(score);
    }

    this.incrementBlockCount(blockId);

    this.removeBlocks(bodyA, bodyB);

    const nextBlockData = this.blockManager.getBlockData(nextBlockId);
    if (nextBlockData) {
      const newBlock = new FallingBlock(
        nextBlockData,
        this.physicsEngine,
        newX,
        newY,
      );

      newBlock.startFalling();

      this.container.addChild(newBlock);
      this.activeBlocks?.add(newBlock);
    }
  }

  private removeBlocks(
    bodyA: ExtendedMatterBody,
    bodyB: ExtendedMatterBody,
  ): void {
    bodyA.isMerging = true;
    bodyB.isMerging = true;

    const blockA = bodyA.fallingBlock as FallingBlock | undefined;
    const blockB = bodyB.fallingBlock as FallingBlock | undefined;

    this.physicsEngine.removeBody(bodyA);
    this.physicsEngine.removeBody(bodyB);

    if (blockA) {
      this.activeBlocks?.delete(blockA);
      blockA.destroy();
    }
    if (blockB) {
      this.activeBlocks?.delete(blockB);
      blockB.destroy();
    }
  }

  destroy(): void {
    if (this.collisionCallback) {
      this.physicsEngine.offCollision(this.collisionCallback);
      this.collisionCallback = null;
    }
    this.mergeQueue = [];
  }
}
